import { useEffect, useRef, useState } from "react";
import { Headset, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { playNotify } from "../lib/notifySound";
import { useAuth } from "../context/AuthContext";
import { ChatPanel } from "../components/ChatPanel";
import { ChatWindow } from "../components/ChatWindow";
import PinConfirm from "../components/PinConfirm";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { MyMentionsCard } from "../components/MyMentionsCard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const STATUS_STYLE = {
  baru: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  diproses: "bg-sky-500/10 text-sky-500 border-sky-500/20",
  selesai: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};
const STATUS_LABEL = { baru: "Baru", diproses: "Diproses", selesai: "Selesai" };
const fmtEst = (v) => (v ? v.replace("T", " ") : "-");

export default function WifiComplaints() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const isSuper = me?.role === "admin";
  const isOwner = isSuper && !!me?.is_owner;
  const [list, setList] = useState([]);
  const [threads, setThreads] = useState([]);
  const [chatAgent, setChatAgent] = useState(null);
  const [pinHapusOpen, setPinHapusOpen] = useState(false);
  const [hapusBusy, setHapusBusy] = useState(false);
  const [delThread, setDelThread] = useState(null);
  const [selected, setSelected] = useState({});
  const [delMany, setDelMany] = useState(false);
  const selectedChannels = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = threads.length > 0 && selectedChannels.length === threads.length;
  const toggleAll = () => setSelected(allSelected ? {} : Object.fromEntries(threads.map((t) => [t.channel, true])));
  const [chatTicket, setChatTicket] = useState(null);
  const [unread, setUnread] = useState({});

  const knownIds = useRef(null);
  const load = () => api.get("/wifi-complaints").then((r) => {
    const data = r.data;
    if (isAdmin && knownIds.current !== null) {
      const fresh = data.filter((c) => !knownIds.current.has(c.id) && c.status === "baru");
      if (fresh.length) {
        playNotify();
        toast.info(`Komplain baru masuk: ${fresh[0].name} — ${fresh[0].category}`);
      }
    }
    knownIds.current = new Set(data.map((c) => c.id));
    setList(data);
  }).catch(() => {});
  const loadUnread = () => api.get("/chat/unread").then((r) => setUnread(r.data)).catch(() => {});
  const loadThreads = () => { if (isAdmin) api.get("/chat/agent-threads").then((r) => setThreads(r.data)).catch(() => {}); };
  useEffect(() => {
    load();
    loadUnread();
    loadThreads();
    const tick = () => { if (document.hidden) return; load(); loadUnread(); loadThreads(); };
    const t = setInterval(tick, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hapusRiwayat = async () => {
    setHapusBusy(true);
    try {
      const { data } = await api.delete("/wifi-complaints/history");
      toast.success(`${data.deleted} riwayat komplain dihapus`);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setHapusBusy(false); }
  };

  const hapusThread = async () => {
    try {
      const { data } = await api.delete(`/chat/${delThread.channel}/thread`);
      toast.success(`Percakapan ${delThread.agent_name} dihapus (${data.deleted} pesan)`);
      setDelThread(null);
      loadThreads(); loadUnread();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const hapusTerpilih = async () => {
    try {
      const { data } = await api.post("/chat/threads/delete-many", { channels: selectedChannels });
      toast.success(`${data.threads} percakapan dihapus (${data.deleted} pesan)`);
      setDelMany(false); setSelected({});
      loadThreads(); loadUnread();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="complaints-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Chat Admin</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isAdmin ? "Daftar keluhan agen terkait layanan WiFi — chat live langsung dengan agen." : "Sampaikan keluhan apapun terkait kendala WiFi — langsung chat live dengan admin."}
        </p>
      </div>

      {isAdmin && <MyMentionsCard onOpen={(ch) => { const t = threads.find((x) => x.channel === ch); if (t) setChatAgent(t); else window.dispatchEvent(new CustomEvent("fasnet:open-chat", { detail: ch })); }} />}

      {isAdmin && threads.length > 0 && (
        <Card className="bg-card border-border shadow-sm" data-testid="agent-threads-card">
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base font-heading flex items-center gap-2"><MessageCircle className="w-4 h-4 text-sky-500" /> Live Chat Agen</CardTitle>
            {isOwner && (
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="threads-select-all" /> Pilih semua
                </label>
                {selectedChannels.length > 0 && (
                  <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => setDelMany(true)} data-testid="threads-delete-selected">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Terpilih ({selectedChannels.length})
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {threads.map((t) => (
                <div key={t.channel} className={`flex items-center hover:bg-accent/50 transition-colors ${selected[t.channel] ? "bg-rose-500/5" : ""}`}>
                {isOwner && <Checkbox className="ml-3" checked={!!selected[t.channel]} onCheckedChange={(c) => setSelected({ ...selected, [t.channel]: !!c })} data-testid={`thread-select-${t.channel}`} />}
                <button type="button" onClick={() => setChatAgent(t)} className="flex-1 min-w-0 text-left px-4 py-2.5 flex items-center gap-3" data-testid={`thread-${t.channel}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{t.kind === "system" && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-500/10 text-sky-400 border border-sky-500/30 mr-1">SISTEM</span>}{t.kind === "portal" && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F04E23]/10 text-[#F97316] border border-[#F04E23]/30 mr-1" data-testid={`thread-portal-${t.channel}`}>PORTAL</span>}{t.kind === "agent" && t.last_by_system && <span className="text-sky-400 mr-1" data-testid={`thread-sys-prefix-${t.channel}`}>Sistem →</span>}{t.agent_name} {t.agent_code && <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">{t.agent_code}</span>} {t.pending && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 text-amber-500 border border-amber-500/30" data-testid={`thread-pending-${t.channel}`}>Aktivasi</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.last_text}</div>
                  </div>
                  {t.unread > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid={`thread-unread-${t.channel}`}>{t.unread}</span>}
                </button>
                {isOwner && (
                  <button type="button" onClick={() => setDelThread(t)} title="Hapus percakapan" className="p-2 mr-2 rounded-md text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-colors" data-testid={`thread-delete-${t.channel}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!isAdmin && me?.id && (
        <Card className="bg-card border-border shadow-sm" data-testid="complaint-chat-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Headset className="w-4 h-4 text-rose-500" /> Live Chat dengan Admin</CardTitle></CardHeader>
          <CardContent><ChatPanel channel={`agent:${me.id}`} testid="complaint-chat" /></CardContent>
        </Card>
      )}

      {isAdmin && (
      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="complaints-list-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-heading">Semua Komplain Masuk</CardTitle>
          {isSuper && list.length > 0 && (
            <Button size="sm" variant="destructive" onClick={() => setPinHapusOpen(true)} data-testid="btn-hapus-riwayat">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Riwayat
            </Button>
          )}
        </CardHeader>
        <Table className="min-w-[880px]" data-testid="complaints-table">
          <TableHeader>
            <TableRow>
              <TableHead>Waktu</TableHead>
              {isAdmin && <TableHead>Agen</TableHead>}
              <TableHead>Cabang</TableHead><TableHead>Kategori</TableHead><TableHead>Keluhan</TableHead><TableHead>Status</TableHead>
              <TableHead>Estimasi Pengerjaan</TableHead><TableHead>Dikerjakan Oleh</TableHead>
              <TableHead className="text-right">Chat</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!list.length && <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-8 text-muted-foreground" data-testid="complaints-empty">Belum ada komplain</TableCell></TableRow>}
            {list.map((c) => (
              <TableRow key={c.id} data-testid={`complaint-row-${c.id}`}>
                <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{fmtDateTime(c.created_at)}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <div className="font-medium text-sm">{c.name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{c.email}</div>
                  </TableCell>
                )}
                <TableCell className="text-sm font-medium whitespace-nowrap">{c.router_name || "-"}</TableCell>
                <TableCell className="text-sm font-medium whitespace-nowrap">{c.category}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[240px]"><span className="line-clamp-2">{c.description}</span></TableCell>
                <TableCell>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-mono border ${STATUS_STYLE[c.status] || STATUS_STYLE.baru}`} data-testid={`complaint-status-${c.id}`}>
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs font-mono">{fmtEst(c.estimation)}</TableCell>
                <TableCell className="text-sm">{c.handled_by || "-"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" className="text-sky-500 relative" title="Chat" onClick={() => setChatTicket(c)} data-testid={`btn-chat-${c.id}`}>
                    <MessageCircle className="w-4 h-4" />
                    {unread[`complaint:${c.id}`] > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-4 h-4 px-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${c.id}`}>
                        {unread[`complaint:${c.id}`]}
                      </span>
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      )}

      {chatTicket && (
        <ChatWindow
          title={<>Chat Komplain — {chatTicket.category} ({chatTicket.router_name || "-"})</>}
          channel={`complaint:${chatTicket.id}`}
          testid="dialog-chat"
          onClose={() => { setUnread((u) => ({ ...u, [`complaint:${chatTicket.id}`]: 0 })); setChatTicket(null); }}
        />
      )}

      {chatAgent && (
        <ChatWindow
          title={<>Chat — {chatAgent.agent_name} {chatAgent.agent_code && <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">{chatAgent.agent_code}</span>}</>}
          channel={chatAgent.channel}
          testid="dialog-agent-chat"
          onClose={() => setChatAgent(null)}
        />
      )}

      <PinConfirm open={!!delThread} onOpenChange={(o) => !o && setDelThread(null)} title={`Hapus Percakapan ${delThread?.agent_name || ""}?`} description="Seluruh pesan di percakapan ini akan dihapus permanen dari layar admin maupun agen." confirmLabel="Ya, Hapus" destructive onConfirm={hapusThread} testid="pin-hapus-thread" />
      <PinConfirm open={delMany} onOpenChange={(o) => !o && setDelMany(false)} title={`Hapus ${selectedChannels.length} Percakapan Terpilih?`} description="Seluruh pesan di percakapan yang dipilih akan dihapus permanen dari layar admin maupun agen." confirmLabel="Ya, Hapus Semua" destructive onConfirm={hapusTerpilih} testid="pin-hapus-threads-many" />
      <PinConfirm open={pinHapusOpen} onOpenChange={setPinHapusOpen} title="Hapus Semua Riwayat Komplain?" description="Seluruh riwayat komplain WiFi beserta chat tiketnya akan dihapus permanen. Tindakan ini tidak bisa dibatalkan." confirmLabel={hapusBusy ? "Menghapus..." : "Ya, Hapus Semua"} destructive onConfirm={hapusRiwayat} testid="pin-hapus-riwayat" />
    </div>
  );
}
